import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import { formatExchangeName } from '../utils/exchangeDisplay';
import './TradeHistory.css';

const TABS = [
  { key: 'orders', label: '注文/決済' },
  { key: 'funding', label: 'FR損益' },
];
const TAB_KEYS = TABS.map(t => t.key);

const TradeHistory = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = TAB_KEYS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'orders';
  const [activeTab, setActiveTab] = useState(initialTab);

  // 注文/決済 (TradeHistory)
  const [trades, setTrades] = useState([]);
  const [tradesPagination, setTradesPagination] = useState({ page: 1, pages: 1, total: 0 });

  // FR損益 (FundingHistory)
  const [fundingRecords, setFundingRecords] = useState([]);
  const [fundingPagination, setFundingPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [fundingSyncing, setFundingSyncing] = useState(false);

  const [loading, setLoading] = useState(true);
  const [selectedExchange, setSelectedExchange] = useState('all');
  const [exchanges, setExchanges] = useState([]);

  useEffect(() => {
    loadExchanges();
    // 初期値で URL に tab が無い場合は反映 (履歴を汚さないよう replace)
    if (!searchParams.get('tab')) {
      setSearchParams({ tab: initialTab }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === 'orders') {
      fetchTrades();
    } else {
      fetchFundingHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedExchange, tradesPagination.page, fundingPagination.page]);

  const loadExchanges = async () => {
    try {
      const res = await api.get('/apikey');
      const keys = res.data.apiKeys || [];
      setExchanges(keys.map(k => k.exchange));
    } catch (error) {
      console.error('Load exchanges error:', error);
    }
  };

  const fetchTrades = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: tradesPagination.page,
        limit: 20,
      });
      if (selectedExchange && selectedExchange !== 'all') params.append('exchange', selectedExchange);
      const response = await api.get(`/trading/history?${params}`);
      setTrades(response.data.trades);
      setTradesPagination(response.data.pagination);
    } catch (error) {
      console.error('Fetch trades error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFundingHistory = async (refresh = false) => {
    setLoading(true);
    if (refresh) setFundingSyncing(true);
    try {
      const params = new URLSearchParams({
        page: fundingPagination.page,
        limit: 20,
      });
      if (selectedExchange && selectedExchange !== 'all') params.append('exchange', selectedExchange);
      if (refresh) params.append('refresh', '1');
      const response = await api.get(`/trading/funding-history?${params}`);
      setFundingRecords(response.data.records);
      setFundingPagination(response.data.pagination);
    } catch (error) {
      console.error('Fetch funding history error:', error);
      toast.error('FR履歴の取得に失敗しました');
    } finally {
      setLoading(false);
      setFundingSyncing(false);
    }
  };

  const exportCSV = async () => {
    try {
      const response = await api.get('/trading/export-csv', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'trade_history.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('ja-JP');
  };

  const formatNumber = (num, decimals = 8) => {
    if (num === undefined || num === null) return '-';
    return parseFloat(num).toFixed(decimals);
  };

  const onExchangeChange = (e) => {
    setSelectedExchange(e.target.value);
    setTradesPagination(p => ({ ...p, page: 1 }));
    setFundingPagination(p => ({ ...p, page: 1 }));
  };

  const renderOrdersTab = () => (
    <>
      {trades.length > 0 ? (
        <>
          <div className="table-wrapper">
            <table className="trades-table">
              <thead>
                <tr>
                  <th>日時</th>
                  <th>取引所</th>
                  <th>シンボル</th>
                  <th>市場</th>
                  <th>売買</th>
                  <th>数量</th>
                  <th>価格</th>
                  <th>手数料</th>
                  <th>実現損益</th>
                  <th>ステータス</th>
                </tr>
              </thead>
              <tbody>
                {trades.map((trade) => (
                  <tr key={trade._id}>
                    <td className="date">{formatDate(trade.executedAt)}</td>
                    <td>{formatExchangeName(trade.exchange)}</td>
                    <td className="symbol">{trade.symbol}</td>
                    <td>{(trade.tradeType || '').includes('Spot') ? '現物' : '先物'}</td>
                    <td className={trade.side === 'Buy' ? 'buy' : 'sell'}>
                      {trade.side === 'Buy' ? '買' : '売'}
                    </td>
                    <td>{trade.quantity}</td>
                    <td>{formatNumber(trade.price, 2)}</td>
                    <td>{formatNumber(trade.fee, 8)}</td>
                    <td className={trade.realizedPnl >= 0 ? 'profit' : 'loss'}>
                      {formatNumber(trade.realizedPnl, 8)}
                    </td>
                    <td>
                      <span className={`status ${trade.status.toLowerCase()}`}>
                        {trade.status === 'Filled' ? '約定' : trade.status === 'Rejected' ? '拒否' : trade.status === 'New' ? '未約定' : trade.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pagination">
            <button
              onClick={() => setTradesPagination(p => ({ ...p, page: p.page - 1 }))}
              disabled={tradesPagination.page === 1}
            >前へ</button>
            <span className="page-info">
              {tradesPagination.page} / {tradesPagination.pages} ページ（全 {tradesPagination.total} 件）
            </span>
            <button
              onClick={() => setTradesPagination(p => ({ ...p, page: p.page + 1 }))}
              disabled={tradesPagination.page === tradesPagination.pages}
            >次へ</button>
          </div>
        </>
      ) : (
        <div className="empty-message">取引履歴がありません</div>
      )}
    </>
  );

  const renderFundingTab = () => (
    <>
      {fundingRecords.length > 0 ? (
        <>
          <div className="table-wrapper">
            <table className="trades-table">
              <thead>
                <tr>
                  <th>日時</th>
                  <th>取引所</th>
                  <th>シンボル</th>
                  <th>種別</th>
                  <th>FR損益</th>
                  <th>USD換算</th>
                  <th>備考</th>
                </tr>
              </thead>
              <tbody>
                {fundingRecords.map((rec) => (
                  <tr key={rec._id}>
                    <td className="date">{formatDate(rec.occurredAt)}</td>
                    <td>{formatExchangeName(rec.exchange)}</td>
                    <td className="symbol">{rec.symbol}</td>
                    <td>{rec.recordType === 'position' ? 'ポジション集計' : '徴収/受取'}</td>
                    <td className={rec.amount >= 0 ? 'profit' : 'loss'}>
                      {formatNumber(rec.amount, 8)} {rec.marginCoin}
                    </td>
                    <td className={rec.amountUsd >= 0 ? 'profit' : 'loss'}>
                      {formatNumber(rec.amountUsd, 4)}
                    </td>
                    <td className="date">
                      {rec.recordType === 'position' && rec.positionOpenAt
                        ? `保有: ${formatDate(rec.positionOpenAt)} 〜 ${formatDate(rec.positionCloseAt)}`
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pagination">
            <button
              onClick={() => setFundingPagination(p => ({ ...p, page: p.page - 1 }))}
              disabled={fundingPagination.page === 1}
            >前へ</button>
            <span className="page-info">
              {fundingPagination.page} / {fundingPagination.pages} ページ（全 {fundingPagination.total} 件）
            </span>
            <button
              onClick={() => setFundingPagination(p => ({ ...p, page: p.page + 1 }))}
              disabled={fundingPagination.page === fundingPagination.pages}
            >次へ</button>
          </div>
        </>
      ) : (
        <div className="empty-message">FR損益履歴がありません</div>
      )}
    </>
  );

  return (
    <div className="trade-history">
      <div className="page-header">
        <h1>損益履歴</h1>
        <div className="header-actions">
          <select
            value={selectedExchange}
            onChange={onExchangeChange}
            className="filter-select"
          >
            <option value="all">全取引所</option>
            {exchanges.map(ex => (
              <option key={ex} value={ex}>
                {formatExchangeName(ex)}
              </option>
            ))}
          </select>
          {activeTab === 'orders' && (
            <button onClick={exportCSV} className="export-btn">CSV出力</button>
          )}
          {activeTab === 'funding' && (
            <button
              onClick={() => {
                const ok = window.confirm(
                  '【取引所と同期】\n\n' +
                  '各取引所から過去 30 日分の FR (Funding Rate) の受取・支払履歴を取得して、この画面に反映します。\n\n' +
                  '・データの取得・反映のみで、新規の発注や決済は一切行いません\n' +
                  '・取引所側の履歴と照合して重複は除外されるので、何度押しても安全です\n' +
                  '・通常は 1 時間ごとに自動で反映されますが、直近の FR 確定をすぐ確認したい時に手動で実行します\n\n' +
                  '実行しますか？'
                );
                if (ok) fetchFundingHistory(true);
              }}
              className="export-btn"
              disabled={fundingSyncing}
            >
              {fundingSyncing ? '同期中...' : '取引所と同期'}
            </button>
          )}
        </div>
      </div>

      <div className="exchange-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`exchange-tab ${activeTab === t.key ? 'active' : ''}`}
            onClick={() => {
              setActiveTab(t.key);
              setSearchParams({ tab: t.key });
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="page-loading">読み込み中...</div>
      ) : activeTab === 'orders' ? renderOrdersTab() : renderFundingTab()}
    </div>
  );
};

export default TradeHistory;
